/**
 * ONE TAP — third-person character rig.
 *
 * There is no skeletal animation data in this project and there never will be. Every pose
 * below is solved from `PlayerState` on the frame it is drawn: hip height, two-bone leg IK
 * against moving foot targets, two-bone arm IK against hand targets, and a stack of additive
 * offsets for lean, recoil, flinch and one-shot beats.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE THREE READS (DESIGN.md §4, §7)
 *
 * At a glance, from any distance, against any background, a player must answer:
 *
 *   (a) WHICH TEAM?   Team colour owns the torso, shoulders, thighs, crest and visor, and a
 *                     fresnel rim shell burns the silhouette's edge in team colour so an
 *                     enemy pops off dark concrete *and* off a bright neon wall.
 *   (b) ARMED?        An armed player visibly carries the gun — and the gun itself is a hot
 *                     yellow lamp when chambered (see gunModel.ts). Their whole posture
 *                     changes: weapon up, torso squared, off-hand supporting.
 *   (c) WOUNDED?      A visible hunch, a clutched abdomen, a limping stride, a slower arm
 *                     swing, a pulsing red rim — and a bleed trail of drips left in world
 *                     space behind them, which is enemy intel by design.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE FOURTH READ: THE PASS
 *
 * A throw must be legible from across the map, because reading a pass early is the only way
 * an enemy gets to contest it — that readability *is* the counterplay (DESIGN.md §1). So the
 * wind-up is enormous: the torso coils away, the throwing arm cocks behind the head, the gun
 * is raised clear of the silhouette, and the rim flares. The follow-through whips the whole
 * body through the throw. Catches get the mirror treatment: hands snap out to meet the gun.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Client-only: imports `three`. Never import this from `src/shared`.
 */

import * as THREE from 'three';
import {
  PLAYER_HEIGHT,
  PLAYER_CROUCH_HEIGHT,
  PLAYER_RADIUS,
  HEAD_HEIGHT,
  HEAD_RADIUS,
  RUN_SPEED,
  SLIDE_MAX_SPEED,
  DASH_DURATION_MS,
  THROW_CHARGE_MS,
  FIRE_COOLDOWN_MS,
  WALLRUN_CAMERA_ROLL,
  WOUNDED_BLEED_TRAIL,
  PULSE_COOLDOWN_MS,
  PITCH_LIMIT,
  TEAM_COLORS,
  PALETTE,
} from '@shared/constants';
import { MoveState, HealthState, type PlayerState } from '@shared/types';
import { clamp, clamp01, damp, lerpN, remap, angleDelta, hashString, TAU } from '@shared/math';
import { makeToonMaterial, attachOutline, setBloomMask } from '@client/render/toon';
import { buildGunMesh, styleGun, animateGun, disposeGunMesh } from '@client/entities/gunModel';
import {
  Joint,
  Impulse,
  MeshBatcher,
  Timeline,
  aimBone,
  bevelBox,
  bell,
  dampAngle,
  disposeObject3D,
  easeOutBack,
  easeOutCubic,
  footPath,
  makeFbm1,
  markOwned,
  markShared,
  mixHex,
  place,
  solveTwoBoneIK,
  taperBox,
  window01,
} from '@client/entities/anim';

// ---------------------------------------------------------------------------
// Proportions
//
// Everything derives from PLAYER_HEIGHT / PLAYER_RADIUS / HEAD_* so the visual body can
// never drift away from the capsule the simulation actually collides and shoots. The head
// in particular is pinned to HEAD_HEIGHT/HEAD_RADIUS: headshots are instant kills, so the
// head you see must be the head you hit.
// ---------------------------------------------------------------------------

const H = PLAYER_HEIGHT;
const CROUCH_RATIO = PLAYER_CROUCH_HEIGHT / PLAYER_HEIGHT;

const HIP_Y = H * 0.515;
const THIGH_LEN = H * 0.235;
const SHIN_LEN = H * 0.235;
const ANKLE_Y = HIP_Y - THIGH_LEN - SHIN_LEN;
const HIP_X = PLAYER_RADIUS * 0.44;

const SHOULDER_Y = H * 0.8;
const SHOULDER_X = PLAYER_RADIUS * 0.76;
const UPPER_LEN = H * 0.168;
const FORE_LEN = H * 0.152;

const CHEST_Y = H * 0.685;
const HEAD_R = HEAD_RADIUS * 0.94;

const TORSO_W = PLAYER_RADIUS * 1.3;
const TORSO_D = PLAYER_RADIUS * 0.82;

/** Shoulder height expressed in pelvis-local space — arms live under `torso`. */
const SHOULDER_LOCAL_Y = SHOULDER_Y - HIP_Y;
const HEAD_LOCAL_Y = HEAD_HEIGHT - HIP_Y;

/**
 * Metres of ground covered per full two-step gait cycle at a dead run.
 *
 * Playtest, twice: "the running is still way too fast — the speed of the animations, not the
 * actual speed of movement. It sounds like an ant crawling. It feels like a really high-gear
 * bicycle." My first pass (3.4 -> 4.8) was far too timid. The ask was explicit: HALF as fast
 * or less.
 *
 * At 9.6m a full gait cycle takes ~0.83s at RUN_SPEED — roughly 2.4 footfalls per second,
 * down from nearly 6. That is a long, loping, weighted stride.
 *
 * The principle, and the thing I got wrong: **a runner going faster lengthens their STRIDE,
 * they do not spin their legs faster.** Cadence is nearly constant across human running
 * speeds; stride length is what scales. Driving cycle rate linearly off velocity is what
 * produces the pitter-patter. So cadence is capped hard here and the extra speed is absorbed
 * by the stride-length term in the leg solver, which reaches further per step instead.
 *
 * Weight comes from slow limbs travelling far, never from fast limbs travelling little.
 */
const STRIDE_LENGTH = 9.6;

/** Hard ceiling on gait cycles per second. Above this a run reads as a cartoon scramble. */
const MAX_GAIT_CYCLES = 1.25;

// One-shot durations (seconds). Tuned so a pass is telegraphed for long enough to contest.
const THROW_ANIM_SEC = 0.46;
const CATCH_ANIM_SEC = 0.34;
const FIRE_ANIM_SEC = FIRE_COOLDOWN_MS / 1000;
const DEATH_ANIM_SEC = 0.62;

/** How long the PULSE shove pose plays for, measured forward from the moment it fired. */
const PULSE_ANIM_MS = 300;

/** Hot orange the rim shifts toward when a player is wounded. Legible over either team hue. */
const WOUND_RIM_TINT = 0xff5a3c;

/**
 * Character outline weight, as a multiple of the global hairline.
 *
 * The arena is white architecture (DESIGN.md §4) and a player has to be findable against a
 * white wall at forty metres. The global line is tuned for architecture, where an elegant
 * hairline is exactly right; on a body it is one pixel of definition trying to hold a
 * silhouette that is the whole point of the object. Bodies get a heavier line — still a line,
 * not comic-book ink, and applied only to the primary mass of each bone.
 */
const CHARACTER_OUTLINE = 1.8;

/**
 * OCCLUSION SILHOUETTE.
 *
 * A name plate drawn through a wall with no body under it is a rendering bug, not a callout:
 * the plate hangs over bare floor, it looks like a broken sprite, and it lies about where
 * your teammate is standing (you read the plate's screen position as their position, when
 * they are actually a storey below it).
 *
 * Teammate plates read through geometry on purpose — knowing where your team is, is the
 * whole point of a 2v2 — so the answer is not to hide the plate, it is to give it an owner.
 * These shells draw the body in flat team colour **only where it is behind something**,
 * using `depthFunc: GreaterDepth`: a fragment passes exactly when the depth buffer already
 * holds something nearer, which is the definition of occluded. Unoccluded, the test fails
 * everywhere and the shells cost a handful of fragments.
 *
 * Enemies never get one. A silhouette through walls is intel, and enemy intel has to be
 * earned by looking at them.
 */
const GHOST_ALPHA_CORE = 0.4;
const GHOST_ALPHA_EDGE = 0.95;
/**
 * How far, in metres, the shell is pushed toward the camera before the depth test.
 *
 * The shells run their own program, so their interpolated depth is not bit-identical to the
 * host mesh's even though the geometry and the matrices are. Without a bias, an *unoccluded*
 * teammate speckles with fragments that happen to land a ULP behind their own surface. One
 * and a half centimetres is far more than that error and far less than any wall a body can
 * hide behind.
 */
const GHOST_DEPTH_BIAS = 0.015;

/**
 * Name-plate frustum gate. The plate floats 2.16m above the feet, so a body that has dropped
 * off the bottom of the screen can still have its plate on screen — another orphan. The
 * owner is tested as a sphere of this radius about their waist, with the margin below
 * expressed in NDC so it is resolution independent.
 */
const TAG_OWNER_RADIUS = 0.7;
/** Fade rate for the plate when its owner enters or leaves frame. Fast, but not a pop. */
const TAG_FADE_RATE = 16;

/**
 * The carrier lamp: a chest panel and a matching one on the backpack that answer "is that
 * player armed, and is the round live?" from **any** angle.
 *
 * The gun is the primary armed read and it is a good one, but it is held in front of the
 * body — from behind, which is where half of all sightlines in an arena shooter come from,
 * an armed player and an unarmed one are the same silhouette in the same colour. The lamp
 * is the same amber/grey/off vocabulary the gun uses, so it needs no learning.
 */
const CARRIER_HOT = 0.85;
const CARRIER_COLD = 0.0;
/** Unlit carrier panel: a dark plate, so an unarmed player has no lamp at all. */
const CARRIER_DARK = mixHex(PALETTE.ink, PALETTE.shadow, 0.3);

/**
 * Suit emissive, healthy and wounded, plus how far the team panels are pulled toward ink
 * when hurt.
 *
 * The wounded read already had posture, a limp, a guttering visor, a pulsing rim and a
 * bleed trail — every one of which is a *hue* or a *shape* cue seen from a particular side.
 * This is the value cue: a hurt player is visibly darker from any angle and in greyscale,
 * which is the one channel that no background, no team colour and no colour-blindness can
 * take away. Kept under the post chain's 0.3 bloom gate at both ends: a body panel is
 * lit, never an emitter.
 */
const SUIT_EMISSIVE = 0.26;
const SUIT_EMISSIVE_WOUNDED = 0.05;
const WOUND_SUIT_DARKEN = 0.3;

/**
 * Visor emissive when healthy. Often the first thing you actually resolve at range.
 *
 * Held below the old 2.6: the post chain adds a blurred copy of every masked emitter back
 * on top of itself before tonemapping, so past about two units the visor's weaker channels
 * are dragged up with the saturated one and an AZURE player ends up wearing a cyan lamp and
 * a SIGNAL player a pink one. Team colour is the read; a lamp that has drifted off the team
 * hue is worse than a dimmer one that has not.
 */
const VISOR_HOT = 1.9;
/**
 * …and when wounded. Nearly out, not merely tinted.
 *
 * This used to be 0.9 with a shift toward the wound orange, which did nothing for SIGNAL:
 * red shifted toward orange is red once its red channel has clipped, so half the roster
 * signalled "hurt" by looking exactly the same. VALUE is the channel that survives every
 * team hue and every background, so the wounded tell is the lamp going *dark* — a guttering
 * ember instead of a headlight, legible the instant an enemy turns to face you.
 */
const VISOR_WOUNDED = 0.26;

/**
 * Name plate and state-icon sizes. `sizeAttenuation` is off on all three sprites, so these
 * are constant fractions of the frame rather than metres — a tag has to stay readable at
 * two metres and at seventy.
 */
const TAG_SPRITE_W = 0.3;
const TAG_SPRITE_H = 0.075;
const ARMED_ICON = 0.05;
const WOUND_ICON = 0.042;

/**
 * Where the two state icons sit relative to the name plate, as sprite `center` offsets.
 *
 * They cannot be positioned in world space. The plate is screen-constant while a world
 * offset shrinks with distance, so the old ±17cm gutter closed to nothing within a few
 * metres and by forty the armed chevron and the wound drop were stamped on top of the
 * player's own name — the exact moment the icons matter most. `Sprite.center` displaces the
 * quad in the same units its scale is measured in, so the gutter now tracks the plate
 * exactly at every distance. Offsets are half a plate plus a little over half an icon, so
 * the two just clear each other.
 */
const ARMED_CENTER_X = 0.5 + (TAG_SPRITE_W * 0.5 + ARMED_ICON * 0.62) / ARMED_ICON;
const WOUND_CENTER_X = 0.5 - (TAG_SPRITE_W * 0.5 + WOUND_ICON * 0.62) / WOUND_ICON;

/** Bleed trail. Emitting faster than this makes a solid line and loses the "trail" read. */
const BLEED_INTERVAL_SEC = 0.16;
const BLEED_LIFE_SEC = 4.5;
const BLEED_CAPACITY = 64;

// ---------------------------------------------------------------------------
// Shared textures + shaders
// ---------------------------------------------------------------------------

let armedIconTexture: THREE.Texture | null = null;
let woundIconTexture: THREE.Texture | null = null;

/** Ink used to outline the tag icons, as a canvas colour. Matches PALETTE.ink. */
const ICON_INK = `#${PALETTE.ink.toString(16).padStart(6, '0')}`;

/**
 * Both state icons are drawn as a WHITE glyph inside a fat ink outline, and both sprites
 * blend normally rather than additively.
 *
 * Additively-blended icons were invisible against the thing they most needed to survive:
 * white architecture in full sun (DESIGN.md §4). Adding amber to a wall that is already at
 * the top of the range changes nothing, so the armed pip vanished over exactly the
 * backdrop most of this arena is made of. Normal blending plus an ink outline reads over
 * white walls, over blue sky and over a body in team colour alike, and because the sprite
 * tint MULTIPLIES the canvas, the outline stays dark while the glyph takes the state colour.
 */
function strokeIconOutline(g: CanvasRenderingContext2D, draw: () => void, weight: number): void {
  g.strokeStyle = ICON_INK;
  g.lineWidth = weight;
  g.lineCap = 'round';
  g.lineJoin = 'round';
  draw();
  g.stroke();
}

/** A fat upward chevron: "this player is holding the gun". */
function getArmedIcon(): THREE.Texture {
  if (armedIconTexture) return armedIconTexture;
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const g = c.getContext('2d');
  if (g) {
    const chevron = (): void => {
      g.beginPath();
      g.moveTo(13, 40);
      g.lineTo(32, 17);
      g.lineTo(51, 40);
    };
    const bar = (): void => {
      g.beginPath();
      g.moveTo(14, 53);
      g.lineTo(50, 53);
    };
    strokeIconOutline(g, chevron, 17);
    strokeIconOutline(g, bar, 16);
    g.strokeStyle = '#ffffff';
    g.lineWidth = 10;
    g.lineCap = 'round';
    g.lineJoin = 'round';
    chevron();
    g.stroke();
    bar();
    g.stroke();
  }
  armedIconTexture = markShared(new THREE.CanvasTexture(c));
  armedIconTexture.colorSpace = THREE.SRGBColorSpace;
  return armedIconTexture;
}

/** A blood drop: "this player is one body shot from dead". */
function getWoundIcon(): THREE.Texture {
  if (woundIconTexture) return woundIconTexture;
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const g = c.getContext('2d');
  if (g) {
    const drop = (): void => {
      g.beginPath();
      g.moveTo(32, 10);
      g.quadraticCurveTo(51, 33, 51, 40);
      g.arc(32, 40, 19, 0, Math.PI);
      g.quadraticCurveTo(13, 33, 32, 10);
      g.closePath();
    };
    g.strokeStyle = ICON_INK;
    g.lineWidth = 9;
    g.lineJoin = 'round';
    drop();
    g.stroke();
    g.fillStyle = '#ffffff';
    drop();
    g.fill();
  }
  woundIconTexture = markShared(new THREE.CanvasTexture(c));
  woundIconTexture.colorSpace = THREE.SRGBColorSpace;
  return woundIconTexture;
}

/**
 * Fresnel rim. This is what stops a player from disappearing into a busy cel-shaded
 * background — the silhouette's grazing edge burns in team colour, so an enemy is
 * findable against concrete, against a white curtain wall and against the skybox alike.
 *
 * BLENDED NORMALLY, NOT ADDITIVELY. Additive was the wrong operator for this arena twice
 * over: adding team colour to a body panel that is already brightly lit pushes it past the
 * tonemap shoulder and the "team colour" rim resolves as a white one, and the rim is at its
 * most needed exactly where the background is white — where an additive edge has nowhere
 * left to go. Replacing the edge pixel with saturated team colour keeps the hue at every
 * exposure, which is the entire job (DESIGN.md §4: colour is the wayfinding).
 *
 * Depth-tested on purpose: it must not shine through walls. Wall-hacks are not a look —
 * the through-geometry read for teammates is the ghost shell below, and it is deliberate.
 */
function makeRimMaterial(color: number, strength: number): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color(color) },
      uPower: { value: 2.4 },
      uStrength: { value: strength },
    },
    vertexShader: /* glsl */ `
      varying vec3 vNormalV;
      varying vec3 vViewPos;
      void main() {
        vNormalV = normalize(normalMatrix * normal);
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vViewPos = mv.xyz;
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      uniform float uPower;
      uniform float uStrength;
      varying vec3 vNormalV;
      varying vec3 vViewPos;
      void main() {
        vec3 v = normalize(-vViewPos);
        float f = 1.0 - clamp(dot(normalize(vNormalV), v), 0.0, 1.0);
        f = pow(f, uPower) * uStrength;
        if (f <= 0.004) discard;
        // Coverage is clamped; the drive above 1 is spent on a little extra punch in the
        // colour instead, so a flaring rim gets hotter without bleaching to white.
        float a = clamp(f, 0.0, 1.0);
        gl_FragColor = vec4(uColor * min(1.0 + (f - 1.0) * 0.35, 1.5), a);
      }
    `,
    transparent: true,
    blending: THREE.NormalBlending,
    depthWrite: false,
    depthTest: true,
    side: THREE.FrontSide,
    toneMapped: false,
  });
}

/**
 * Through-geometry team silhouette — see GHOST_ALPHA_CORE. Flat team colour with the edges
 * pushed to near-opaque, so at forty metres through a floor it still resolves as a body
 * rather than as a coloured smudge.
 *
 * `side: FrontSide` matters: on a closed mesh only the near hull faces the camera, so the
 * shell is one silhouette and not the near and far halves of the body layered over each
 * other. Normal blending, because the surface it most has to survive is a white wall in
 * full sun, and additive has nothing left to add there.
 */
function makeGhostMaterial(color: number): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color(color) },
      uFade: { value: 1 },
    },
    vertexShader: /* glsl */ `
      varying vec3 vNormalV;
      varying vec3 vViewPos;
      void main() {
        vNormalV = normalize(normalMatrix * normal);
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vViewPos = mv.xyz;
        // View space looks down -Z, so adding to z moves the shell toward the camera.
        mv.z += ${GHOST_DEPTH_BIAS.toFixed(4)};
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      uniform float uFade;
      varying vec3 vNormalV;
      varying vec3 vViewPos;
      void main() {
        vec3 v = normalize(-vViewPos);
        float f = 1.0 - clamp(dot(normalize(vNormalV), v), 0.0, 1.0);
        float a = mix(${GHOST_ALPHA_CORE.toFixed(3)}, ${GHOST_ALPHA_EDGE.toFixed(3)}, pow(f, 1.6)) * uFade;
        if (a <= 0.004) discard;
        gl_FragColor = vec4(uColor, a);
      }
    `,
    transparent: true,
    blending: THREE.NormalBlending,
    depthWrite: false,
    depthTest: true,
    // The whole trick: pass only where something nearer has already been drawn.
    depthFunc: THREE.GreaterDepth,
    side: THREE.FrontSide,
    toneMapped: false,
  });
}

/** Points shader for the bleed trail: soft dark drips that fade out over a few seconds. */
function makeBleedMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uColor: { value: new THREE.Color(0x8c0f2a) } },
    vertexShader: /* glsl */ `
      attribute float aAlpha;
      attribute float aSize;
      varying float vAlpha;
      void main() {
        vAlpha = aAlpha;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = aSize * 260.0 / max(0.001, -mv.z);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      varying float vAlpha;
      void main() {
        vec2 d = gl_PointCoord - 0.5;
        float r = dot(d, d);
        if (r > 0.25 || vAlpha <= 0.002) discard;
        float a = vAlpha * smoothstep(0.25, 0.06, r);
        gl_FragColor = vec4(uColor, a);
      }
    `,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    toneMapped: false,
  });
}

// ---------------------------------------------------------------------------
// Scratch — the rig runs once per player per frame; it must not allocate.
// ---------------------------------------------------------------------------

const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3();
const _knee = new THREE.Vector3();
const _pole = new THREE.Vector3();
const _root = new THREE.Vector3();
const _target = new THREE.Vector3();
const _end = new THREE.Vector3();
const _mat = new THREE.Matrix4();
const _inv = new THREE.Matrix4();
const _quat = new THREE.Quaternion();
const _ofA = new THREE.Vector3();
const _ofB = new THREE.Vector3();
const _ofC = new THREE.Vector3();
const _ofD = new THREE.Vector3();
const UP = new THREE.Vector3(0, 1, 0);
const AXIS_X = new THREE.Vector3(1, 0, 0);
const STRAIGHT_DOWN = new THREE.Vector3(0, -1, 0);
const FOOT_TARGET_L = new THREE.Vector3();
const FOOT_TARGET_R = new THREE.Vector3();
const HAND_TARGET_L = new THREE.Vector3();
const HAND_TARGET_R = new THREE.Vector3();
const _acc = new THREE.Vector3();
const _frustum = new THREE.Frustum();
const _projScreen = new THREE.Matrix4();
const _ownerSphere = new THREE.Sphere(new THREE.Vector3(), TAG_OWNER_RADIUS);

/** Weighted-average accumulator: `acc += target * w`, divided by total weight at the end. */
function accum(out: THREE.Vector3, x: number, y: number, z: number, w: number): void {
  out.x += x * w;
  out.y += y * w;
  out.z += z * w;
}

/** Build an orientation whose local **-Z** points along `fwd`. The gun is authored -Z forward. */
function orientForward(q: THREE.Quaternion, fwd: THREE.Vector3, up: THREE.Vector3): void {
  _ofA.copy(fwd);
  if (_ofA.lengthSq() < 1e-8) _ofA.set(0, 0, -1);
  _ofA.normalize();
  _ofB.copy(_ofA).multiplyScalar(-1); // object +Z
  _ofC.crossVectors(up, _ofB);
  if (_ofC.lengthSq() < 1e-8) _ofC.set(1, 0, 0);
  _ofC.normalize();
  _ofD.crossVectors(_ofB, _ofC);
  _mat.makeBasis(_ofC, _ofD, _ofB);
  q.setFromRotationMatrix(_mat);
}

// ---------------------------------------------------------------------------
// PlayerRig
// ---------------------------------------------------------------------------

export class PlayerRig {
  readonly object: THREE.Object3D;

  /**
   * Optional. Set this to the render camera and the name tag scales and fades with
   * distance instead of staying a fixed screen size. Purely cosmetic; leaving it null
   * is a valid, working configuration.
   */
  camera: THREE.Camera | null = null;

  // --- scene graph ---------------------------------------------------------
  private readonly body: THREE.Group;
  private readonly pelvis: THREE.Group;
  private readonly torso: THREE.Group;
  private readonly head: THREE.Group;
  private readonly thighL: THREE.Group;
  private readonly thighR: THREE.Group;
  private readonly shinL: THREE.Group;
  private readonly shinR: THREE.Group;
  private readonly footL: THREE.Group;
  private readonly footR: THREE.Group;
  private readonly upperL: THREE.Group;
  private readonly upperR: THREE.Group;
  private readonly foreL: THREE.Group;
  private readonly foreR: THREE.Group;
  private readonly handL: THREE.Group;
  private readonly handR: THREE.Group;
  private readonly gunAnchor: THREE.Group;
  private readonly gunMesh: THREE.Group;

  private readonly tagRoot: THREE.Group;
  private readonly tagSprite: THREE.Sprite;
  private readonly armedSprite: THREE.Sprite;
  private readonly woundSprite: THREE.Sprite;
  private readonly tagTexture: THREE.CanvasTexture;

  private readonly rimMaterials: THREE.ShaderMaterial[] = [];
  /** Through-geometry team silhouettes. Friendlies only — see makeGhostMaterial. */
  private readonly ghostMeshes: THREE.Mesh[] = [];
  private readonly ghostMaterials: THREE.ShaderMaterial[] = [];
  private readonly ownedMaterials: THREE.Material[] = [];
  /** The visor lamp. Guttering out is the front-on half of the wounded read. */
  private readonly visorMaterial: THREE.Material;
  /** Team panels. Their value drops when the player is wounded. */
  private readonly suitMaterial: THREE.Material;
  /** Chest + backpack lamp: the armed read from behind and from the flanks. */
  private readonly carrierMaterial: THREE.Material;

  // --- bleed trail ---------------------------------------------------------
  private readonly bleed: THREE.Points;
  private readonly bleedPos: Float32Array;
  private readonly bleedVel: Float32Array;
  private readonly bleedLife: Float32Array;
  private readonly bleedFloor: Float32Array;
  private readonly bleedAlpha: Float32Array;
  private readonly bleedSize: Float32Array;
  private bleedCursor = 0;
  private bleedTimer = 0;

  // --- joints --------------------------------------------------------------
  private readonly jBody: Joint;
  private readonly jPelvis: Joint;
  private readonly jTorso: Joint;
  private readonly jHead: Joint;
  private readonly jGun: Joint;

  // --- animation state -----------------------------------------------------
  private readonly team: number;
  private readonly noise: (t: number) => number;
  private time = 0;
  private gaitPhase = 0;
  private yawSmooth = 0;
  private pitchSmooth = 0;
  private headYawLag = 0;

  private wGround = 1;
  private wAir = 0;
  private wSlide = 0;
  private wWall = 0;
  private wGrapple = 0;
  private wDash = 0;

  private readonly landDip = new Impulse(7.5, 0.42);
  private readonly fireKick = new Impulse(14, 0.4);
  private readonly flinch = new Impulse(9, 0.35);
  private readonly leanRoll = new Impulse(3.4, 0.9);
  private readonly leanPitch = new Impulse(3.0, 0.9);

  private readonly tlThrow = new Timeline(THROW_ANIM_SEC);
  private readonly tlCatch = new Timeline(CATCH_ANIM_SEC);
  private readonly tlFire = new Timeline(FIRE_ANIM_SEC);
  private readonly tlDeath = new Timeline(DEATH_ANIM_SEC);

  private windup = 0;
  private armedBlend = 0;
  private woundBlend = 0;
  private crouchBlend = 0;
  private throwSide = 1;

  /** Name-plate presence, 0..1. Driven by whether the plate's OWNER is on screen. */
  private tagFade = 0;
  /** Occlusion-silhouette presence, 0..1. Ramps rather than pops, same as the plate. */
  private ghostFade = 0;
  private carrierKey = '';
  /**
   * The camera the scene was last drawn with, captured from the torso's draw call.
   *
   * The plate has to know whether its owner is in frame, and `update()` is only handed
   * `PlayerState`. Callers may set the public `camera` field instead — this is the fallback
   * that makes the frustum gate work without one, and it is filtered to perspective cameras
   * so a shadow pass can never hand us an orthographic light frustum.
   */
  private renderCamera: THREE.Camera | null = null;

  // --- previous-frame snapshot, for event detection ------------------------
  private first = true;
  private prevHeld = -1;
  private prevFireCd = 0;
  private prevCatchLock = 0;
  private prevThrowCharge = 0;
  private prevGrounded = true;
  private prevVelY = 0;
  private prevVelX = 0;
  private prevVelZ = 0;
  private prevYaw = 0;
  private prevHealth: HealthState = HealthState.Healthy;
  private prevLocalTeam = -99;

  /**
   * Chamber state of the gun this player is carrying.
   *
   * `PlayerState` alone cannot know it — `chambered` lives on the `GunEntity`. The rig
   * therefore *infers* it from observable transitions (a catch chambers, a shot empties,
   * a floor pickup is empty, a heal spends the round), which is correct in every normal
   * case. Whoever owns the frame loop and has the `WorldState` should still call
   * `setHeldGunState()` each frame to make it authoritative — it is one line and it removes
   * the only situation the inference can get wrong (a rig created while a player is already
   * holding an empty gun).
   */
  private chambered = true;
  private chamberAuthoritative = false;

  constructor(team: number, name: string) {
    this.team = team === 1 ? 1 : 0;
    this.noise = makeFbm1(hashString(name) ^ 0x9e3779b9);

    const teamColor = TEAM_COLORS[this.team]!;

    const own = <T extends THREE.Material>(m: T): T => {
      this.ownedMaterials.push(m);
      return m;
    };

    // --- materials ---------------------------------------------------------
    // Team colour owns the large flat panels; slate owns the joints, boots and gloves. That
    // contrast is the silhouette.
    //
    // Nothing on a player is white any more. It used to be — chest plate, gloves, jaw and
    // boots were all PALETTE.base — and against white architecture those panels punched
    // holes straight through the silhouette, so a body at range dissolved into whatever wall
    // was behind it. The whole figure now lives on the dark half of the ramp with saturated
    // team colour as the only bright value, which is what makes an enemy findable against a
    // white wall, a blue sky, or another player.
    const matSuit = own(makeToonMaterial({ color: teamColor, emissive: teamColor, emissiveIntensity: 0.26, rim: 0.6 }));
    const matSuitDeep = own(
      makeToonMaterial({ color: mixHex(teamColor, PALETTE.ink, 0.55), emissive: teamColor, emissiveIntensity: 0.14 }),
    );
    /** Joints, boots, gloves, backpack: the darkest value the palette allows. */
    const matInk = own(makeToonMaterial({ color: mixHex(PALETTE.ink, PALETTE.shadow, 0.1), rim: 0.28 }));
    /** Armour plating: a step up from ink, so bevelled panels still read as form. */
    const matPlate = own(makeToonMaterial({ color: mixHex(PALETTE.ink, PALETTE.shadow, 0.36), rim: 0.42 }));
    const matVisor = own(
      makeToonMaterial({ color: teamColor, emissive: teamColor, emissiveIntensity: VISOR_HOT }),
    );
    this.visorMaterial = matVisor;
    this.suitMaterial = matSuit;
    /** Carrier lamp. Starts dark: everyone spawns before the guns are handed out. */
    const matCarrier = own(
      makeToonMaterial({ color: CARRIER_DARK, emissive: PALETTE.loaded, emissiveIntensity: CARRIER_COLD }),
    );
    this.carrierMaterial = matCarrier;

    // --- root --------------------------------------------------------------
    this.object = new THREE.Group();
    this.object.name = `rig:${name}`;

    this.body = new THREE.Group();
    this.body.name = 'body';
    this.object.add(this.body);

    this.pelvis = new THREE.Group();
    this.pelvis.name = 'pelvis';
    this.pelvis.position.set(0, HIP_Y, 0);
    this.body.add(this.pelvis);

    this.torso = new THREE.Group();
    this.torso.name = 'torso';
    this.pelvis.add(this.torso);

    // Static parts are collected per bone and merged into one mesh per material at the end.
    // A cel-shaded character is a lot of little bevelled blocks, and six of them on screen
    // plus an outline pass is a draw-call problem long before it is a triangle problem
    // (DESIGN.md §7: locked 60fps at 1080p on integrated graphics).
    const batchers = new Map<THREE.Object3D, MeshBatcher>();
    const part = (
      parent: THREE.Object3D,
      geo: THREE.BufferGeometry,
      mat: THREE.Material,
      x = 0,
      y = 0,
      z = 0,
    ): void => {
      let b = batchers.get(parent);
      if (!b) {
        b = new MeshBatcher();
        batchers.set(parent, b);
      }
      b.add(mat, geo, place(x, y, z));
    };

    // --- pelvis / torso ----------------------------------------------------
    part(this.pelvis, bevelBox(TORSO_W * 0.84, 0.2, TORSO_D * 0.95, 0.03, 0.05), matSuitDeep, 0, 0.02, 0);
    part(this.pelvis, bevelBox(TORSO_W * 0.9, 0.05, TORSO_D * 1.02, 0.012, 0.02), matInk, 0, -0.07, 0);

    part(this.torso, taperBox(TORSO_W * 0.84, 0.44, TORSO_D, 1.28, 0.03), matSuit, 0, CHEST_Y - HIP_Y, 0);
    // Shoulder pads share the chest's material, so they cost nothing extra.
    for (const s of [1, -1]) {
      part(this.torso, bevelBox(0.17, 0.15, 0.2, 0.025, 0.05), matSuit, s * SHOULDER_X, SHOULDER_LOCAL_Y, 0);
    }
    // Chest plate — bone panel over team colour, the classic JSR two-tone.
    part(this.torso, bevelBox(TORSO_W * 0.5, 0.2, 0.05, 0.012, 0.03), matPlate, 0, CHEST_Y - HIP_Y + 0.05, -TORSO_D * 0.5);
    // Backpack: bulk on the spine so the rear silhouette is not a flat wall.
    part(this.torso, bevelBox(TORSO_W * 0.56, 0.28, 0.16, 0.02, 0.04), matInk, 0, CHEST_Y - HIP_Y + 0.02, TORSO_D * 0.52);
    // Carrier lamps, front and back. Amber when this player is holding a live round, flat
    // grey when they are holding a spent gun, dark when they are holding nothing — the same
    // three-state vocabulary the gun itself uses, so there is nothing new to learn. The rear
    // one is the point of the exercise: the gun is carried in front of the body, so from
    // behind it is the only thing that answers "is that one armed?".
    part(this.torso, bevelBox(TORSO_W * 0.34, 0.042, 0.026, 0.007, 0.012), matCarrier, 0, CHEST_Y - HIP_Y + 0.04, -TORSO_D * 0.5 - 0.03);
    part(this.torso, bevelBox(TORSO_W * 0.34, 0.042, 0.026, 0.007, 0.012), matCarrier, 0, CHEST_Y - HIP_Y + 0.06, TORSO_D * 0.52 + 0.086);
    // Collar.
    part(this.torso, bevelBox(TORSO_W * 0.42, 0.09, TORSO_D * 0.7, 0.02, 0.03), matInk, 0, SHOULDER_LOCAL_Y + 0.03, 0);

    // --- head --------------------------------------------------------------
    this.head = new THREE.Group();
    this.head.name = 'head';
    this.head.position.set(0, HEAD_LOCAL_Y, 0);
    this.torso.add(this.head);

    part(this.head, bevelBox(HEAD_R * 1.82, HEAD_R * 1.76, HEAD_R * 1.9, 0.045, 0.07), matInk, 0, 0, 0);
    // Visor — a wide emissive band. At range this is often the first thing you actually see.
    part(this.head, bevelBox(HEAD_R * 1.55, HEAD_R * 0.6, 0.05, 0.012, 0.02), matVisor, 0, HEAD_R * 0.16, -HEAD_R * 0.95);
    // Jaw / breather.
    part(this.head, bevelBox(HEAD_R * 1.0, HEAD_R * 0.5, 0.09, 0.015, 0.025), matPlate, 0, -HEAD_R * 0.55, -HEAD_R * 0.78);
    // Crest fin — a long flat team-coloured blade along the skull. Deliberately swept back
    // rather than tall: headshots are instant kills, so a flagpole above the head would give
    // players a large, prominent thing to aim at that is *not* the head hitbox (a sphere of
    // HEAD_RADIUS at HEAD_HEIGHT, crowning at 1.82m). This is rooted inside the skull and
    // sits within a couple of centimetres of that crown, so what you shoot is what you hit.
    part(this.head, bevelBox(0.045, HEAD_R * 0.6, HEAD_R * 2.3, 0.012, 0.024), matSuit, 0, HEAD_R * 0.72, HEAD_R * 0.3);
    // Ear pods.
    for (const s of [1, -1]) {
      part(this.head, bevelBox(0.06, HEAD_R * 0.9, HEAD_R * 0.9, 0.015, 0.03), matSuitDeep, s * HEAD_R * 0.95, 0, HEAD_R * 0.1);
    }

    // --- arms (flat chain under torso; positions come from IK) -------------
    const makeArm = (side: number): [THREE.Group, THREE.Group, THREE.Group] => {
      const upper = new THREE.Group();
      upper.name = side > 0 ? 'upperR' : 'upperL';
      this.torso.add(upper);
      part(upper, taperBox(0.115, UPPER_LEN, 0.115, 0.86, 0.02), matSuitDeep, 0, -UPPER_LEN * 0.5, 0);

      const fore = new THREE.Group();
      fore.name = side > 0 ? 'foreR' : 'foreL';
      this.torso.add(fore);
      part(fore, taperBox(0.1, FORE_LEN, 0.1, 0.88, 0.018), matInk, 0, -FORE_LEN * 0.5, 0);
      // Forearm guard in team colour.
      part(fore, bevelBox(0.11, 0.11, 0.045, 0.012, 0.025), matSuit, 0, -FORE_LEN * 0.42, -0.045);

      const hand = new THREE.Group();
      hand.name = side > 0 ? 'handR' : 'handL';
      this.torso.add(hand);
      part(hand, bevelBox(0.1, 0.115, 0.125, 0.02, 0.035), matInk, 0, -0.05, -0.01);

      return [upper, fore, hand];
    };
    [this.upperR, this.foreR, this.handR] = makeArm(1);
    [this.upperL, this.foreL, this.handL] = makeArm(-1);

    // --- legs (flat chain under pelvis) ------------------------------------
    const makeLeg = (side: number): [THREE.Group, THREE.Group, THREE.Group] => {
      const thigh = new THREE.Group();
      thigh.name = side > 0 ? 'thighR' : 'thighL';
      this.pelvis.add(thigh);
      part(thigh, taperBox(0.155, THIGH_LEN, 0.155, 0.84, 0.025), matSuit, 0, -THIGH_LEN * 0.5, 0);

      const shin = new THREE.Group();
      shin.name = side > 0 ? 'shinR' : 'shinL';
      this.pelvis.add(shin);
      part(shin, taperBox(0.125, SHIN_LEN, 0.125, 0.86, 0.02), matInk, 0, -SHIN_LEN * 0.5, 0);
      // Shin plate.
      part(shin, bevelBox(0.11, 0.2, 0.04, 0.012, 0.025), matSuitDeep, 0, -SHIN_LEN * 0.45, -0.055);

      const foot = new THREE.Group();
      foot.name = side > 0 ? 'footR' : 'footL';
      foot.position.set(0, -SHIN_LEN, 0);
      shin.add(foot);
      part(foot, bevelBox(0.14, 0.085, 0.3, 0.02, 0.04), matPlate, 0, -0.02, -0.05);
      part(foot, bevelBox(0.145, 0.03, 0.31, 0.008, 0.02), matInk, 0, -0.062, -0.05);

      return [thigh, shin, foot];
    };
    [this.thighR, this.shinR, this.footR] = makeLeg(1);
    [this.thighL, this.shinL, this.footL] = makeLeg(-1);

    // --- merge + outline ---------------------------------------------------
    // Only the primary mass of each bone gets an inverted hull. Outlining every internal
    // greeble doubles the cost for a line nobody can resolve past three metres.
    const merged = new Map<THREE.Object3D, THREE.Mesh[]>();
    for (const [parent, batcher] of batchers) {
      const list = batcher.flush(parent, { name: parent.name });
      merged.set(parent, list);
      if (list[0]) attachOutline(list[0], CHARACTER_OUTLINE);
    }
    const primary = (parent: THREE.Object3D, material: THREE.Material): THREE.Mesh | null =>
      merged.get(parent)?.find((m) => m.material === material) ?? null;

    // --- rim shells --------------------------------------------------------
    // Applied to the three biggest masses only. Three extra draw calls per player buys the
    // single most important thing in a fast shooter: never losing track of a body.
    const rimHosts = [
      primary(this.torso, matSuit),
      primary(this.head, matInk),
      primary(this.pelvis, matSuitDeep),
    ].filter((m): m is THREE.Mesh => m !== null);
    for (const host of rimHosts) {
      const rimMat = makeRimMaterial(teamColor, 0.7);
      this.rimMaterials.push(rimMat);
      this.ownedMaterials.push(rimMat);
      const shell = new THREE.Mesh(host.geometry, rimMat);
      shell.name = 'rim';
      shell.castShadow = false;
      shell.receiveShadow = false;
      shell.renderOrder = 3;
      host.add(shell);
    }

    // --- occlusion silhouette ---------------------------------------------
    // The masses that make a body legible as a body at range: torso, head, hips and the two
    // legs. Arms and hands are left out — they add draw calls and, at the distance where
    // this matters, nothing to the read. Hidden until the rig learns it is a teammate.
    const ghostHosts = [
      primary(this.torso, matSuit),
      primary(this.head, matInk),
      primary(this.pelvis, matSuitDeep),
      primary(this.thighR, matSuit),
      primary(this.thighL, matSuit),
      primary(this.shinR, matInk),
      primary(this.shinL, matInk),
    ].filter((m): m is THREE.Mesh => m !== null);
    for (const host of ghostHosts) {
      const ghostMat = makeGhostMaterial(teamColor);
      this.ghostMaterials.push(ghostMat);
      this.ownedMaterials.push(ghostMat);
      const shell = new THREE.Mesh(host.geometry, ghostMat);
      shell.name = 'ghost';
      shell.castShadow = false;
      shell.receiveShadow = false;
      // After the rim, and after anything else on the body: it is the last word on where a
      // teammate is, and it must not be depth-sorted behind its own host.
      shell.renderOrder = 4;
      shell.visible = false;
      host.add(shell);
      this.ghostMeshes.push(shell);
    }

    // The plate's frustum gate needs a camera and `update()` is never handed one. Borrow it
    // from the torso's own draw call; it is the last mass to stop being drawn, so if this
    // has never fired there is no plate to place either.
    const torsoMesh = primary(this.torso, matSuit);
    if (torsoMesh) {
      torsoMesh.onBeforeRender = (_renderer, _scene, cam): void => {
        if ((cam as THREE.PerspectiveCamera).isPerspectiveCamera === true) this.renderCamera = cam;
      };
    }

    // --- held gun ----------------------------------------------------------
    this.gunAnchor = new THREE.Group();
    this.gunAnchor.name = 'gunAnchor';
    this.gunAnchor.position.set(0, -0.06, -0.02);
    this.handR.add(this.gunAnchor);

    this.gunMesh = buildGunMesh();
    this.gunMesh.visible = false;
    this.gunAnchor.add(this.gunMesh);
    styleGun(this.gunMesh, this.team, true);

    // --- name tag ----------------------------------------------------------
    this.tagRoot = new THREE.Group();
    this.tagRoot.name = 'tag';
    this.tagRoot.position.set(0, H + 0.36, 0);
    this.object.add(this.tagRoot);

    // The only texture a rig actually owns: its own name plate.
    this.tagTexture = markOwned(makeNameTexture(name, teamColor));
    const tagMat = new THREE.SpriteMaterial({
      map: this.tagTexture,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      sizeAttenuation: false,
      toneMapped: false,
    });
    this.ownedMaterials.push(tagMat);
    this.tagSprite = new THREE.Sprite(tagMat);
    this.tagSprite.scale.set(TAG_SPRITE_W, TAG_SPRITE_H, 1);
    this.tagSprite.renderOrder = 20;
    this.tagRoot.add(this.tagSprite);

    const armedMat = new THREE.SpriteMaterial({
      map: getArmedIcon(),
      color: PALETTE.loaded,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      sizeAttenuation: false,
      toneMapped: false,
    });
    this.ownedMaterials.push(armedMat);
    this.armedSprite = new THREE.Sprite(armedMat);
    this.armedSprite.scale.set(ARMED_ICON, ARMED_ICON, 1);
    this.armedSprite.center.set(ARMED_CENTER_X, 0.5);
    // The quad is displaced several multiples of its own size away from its origin, so the
    // sprite's position-derived bounding sphere no longer contains what is drawn.
    this.armedSprite.frustumCulled = false;
    this.armedSprite.renderOrder = 21;
    this.armedSprite.visible = false;
    this.tagRoot.add(this.armedSprite);

    const woundMat = new THREE.SpriteMaterial({
      map: getWoundIcon(),
      color: PALETTE.danger,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      sizeAttenuation: false,
      toneMapped: false,
    });
    this.ownedMaterials.push(woundMat);
    this.woundSprite = new THREE.Sprite(woundMat);
    this.woundSprite.scale.set(WOUND_ICON, WOUND_ICON, 1);
    this.woundSprite.center.set(WOUND_CENTER_X, 0.5);
    this.woundSprite.frustumCulled = false;
    this.woundSprite.renderOrder = 21;
    this.woundSprite.visible = false;
    this.tagRoot.add(this.woundSprite);

    // --- bleed trail -------------------------------------------------------
    this.bleedPos = new Float32Array(BLEED_CAPACITY * 3);
    this.bleedVel = new Float32Array(BLEED_CAPACITY * 3);
    this.bleedLife = new Float32Array(BLEED_CAPACITY);
    this.bleedFloor = new Float32Array(BLEED_CAPACITY);
    this.bleedAlpha = new Float32Array(BLEED_CAPACITY);
    this.bleedSize = new Float32Array(BLEED_CAPACITY);

    const bleedGeo = new THREE.BufferGeometry();
    bleedGeo.setAttribute('position', new THREE.BufferAttribute(this.bleedPos, 3));
    bleedGeo.setAttribute('aAlpha', new THREE.BufferAttribute(this.bleedAlpha, 1));
    bleedGeo.setAttribute('aSize', new THREE.BufferAttribute(this.bleedSize, 1));
    bleedGeo.setDrawRange(0, BLEED_CAPACITY);
    // The positions in this buffer are world-space, and the object's matrix cancels its own
    // parent transform, so geometry-space bounds are meaningless. Pin a small finite sphere:
    // culling is off anyway, and an enormous one would wreck anything that unions scene
    // bounds (a shadow-camera auto-fit, for instance).
    bleedGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1);

    const bleedMat = makeBleedMaterial();
    this.ownedMaterials.push(bleedMat);
    this.bleed = new THREE.Points(bleedGeo, bleedMat);
    this.bleed.name = 'bleedTrail';
    this.bleed.frustumCulled = false;
    // Positions are authored in world space; cancelling the parent transform each frame is
    // what lets the trail stay behind on the floor instead of riding along with the player.
    this.bleed.matrixAutoUpdate = false;
    this.bleed.visible = false;
    this.object.add(this.bleed);

    // --- joints ------------------------------------------------------------
    this.jBody = new Joint(this.body);
    this.jPelvis = new Joint(this.pelvis);
    this.jTorso = new Joint(this.torso);
    this.jHead = new Joint(this.head);
    this.jGun = new Joint(this.gunAnchor);
  }

  /**
   * Authoritative override for the held gun's chamber state. Call it every frame from the
   * code that owns `WorldState`:
   *
   *   const g = p.heldGun >= 0 ? w.guns[p.heldGun] : null;
   *   rig.setHeldGunState(g ? g.chambered : false);
   *
   * Not part of the fixed module contract (`update()` only receives `PlayerState`), but
   * `chambered` is the most important bit of information in the game and the rig cannot
   * derive it with certainty on its own.
   */
  setHeldGunState(chambered: boolean): void {
    this.chamberAuthoritative = true;
    this.chambered = chambered;
  }

  // -------------------------------------------------------------------------
  // Per-frame update
  // -------------------------------------------------------------------------

  update(p: PlayerState, dtSec: number, localTeam: number): void {
    const dt = clamp(dtSec, 0, 0.1);
    this.time += dt;

    const dead = p.healthState === HealthState.Dead;
    const wounded = p.healthState === HealthState.Wounded;
    const armed = p.heldGun >= 0;

    this.detectEvents(p, dt);

    // --- root transform ----------------------------------------------------
    const teleported = this.first || _v.set(p.pos.x, p.pos.y, p.pos.z).distanceToSquared(this.object.position) > 25;
    this.object.position.set(p.pos.x, p.pos.y, p.pos.z);
    if (teleported) {
      this.yawSmooth = p.yaw;
      this.pitchSmooth = p.pitch;
      this.headYawLag = 0;
      this.leanRoll.reset();
      this.leanPitch.reset();
      this.landDip.reset();
      this.fireKick.reset();
      this.flinch.reset();
      this.clearBleed();
    }

    const yawRate = this.first ? 0 : angleDelta(this.prevYaw, p.yaw) / Math.max(1e-4, dt);
    this.yawSmooth = dampAngle(this.yawSmooth, p.yaw, 26, dt);
    this.pitchSmooth = damp(this.pitchSmooth, clamp(p.pitch, -PITCH_LIMIT, PITCH_LIMIT), 20, dt);
    this.object.rotation.y = this.yawSmooth;

    // --- derived motion ----------------------------------------------------
    const speed = Math.hypot(p.vel.x, p.vel.z);
    const speedN = clamp01(speed / RUN_SPEED);
    const speedHi = clamp01(speed / SLIDE_MAX_SPEED);

    // --- blends ------------------------------------------------------------
    const ms = p.moveState;
    this.wGround = damp(this.wGround, ms === MoveState.Grounded ? 1 : 0, 15, dt);
    this.wAir = damp(this.wAir, ms === MoveState.Airborne ? 1 : 0, 13, dt);
    this.wSlide = damp(this.wSlide, ms === MoveState.Sliding ? 1 : 0, 18, dt);
    this.wWall = damp(this.wWall, ms === MoveState.WallRunning ? 1 : 0, 14, dt);
    this.wGrapple = damp(this.wGrapple, ms === MoveState.Grappling ? 1 : 0, 12, dt);
    this.wDash = damp(this.wDash, ms === MoveState.Dashing ? 1 : 0, 24, dt);

    this.armedBlend = damp(this.armedBlend, armed ? 1 : 0, 11, dt);
    this.woundBlend = damp(this.woundBlend, wounded ? 1 : 0, 5, dt);
    this.crouchBlend = damp(this.crouchBlend, p.crouching ? 1 : 0, 16, dt);

    // Throw wind-up: driven straight off the charge timer, so what an enemy sees is exactly
    // how long the thrower has been holding the button.
    const chargeN = clamp01(p.throwChargeMs / THROW_CHARGE_MS);
    this.windup = damp(this.windup, p.throwChargeMs > 0 ? 0.42 + chargeN * 0.58 : 0, 14, dt);

    // --- springs -----------------------------------------------------------
    this.landDip.update(dt);
    this.fireKick.update(dt);
    this.flinch.update(dt);
    this.leanRoll.kick(clamp(-yawRate, -14, 14) * speedN * dt * 5.5);
    this.leanRoll.update(dt);
    this.leanPitch.update(dt);

    const tThrow = this.tlThrow.update(dt);
    const tCatch = this.tlCatch.update(dt);
    const tFire = this.tlFire.update(dt);
    const tDeath = this.tlDeath.update(dt);

    // --- gait --------------------------------------------------------------
    const grounded = p.grounded || ms === MoveState.Grounded;
    const cycles = grounded ? clamp(speed / STRIDE_LENGTH, 0, MAX_GAIT_CYCLES) : 0;
    this.gaitPhase += cycles * dt;
    if (this.gaitPhase > 1e6) this.gaitPhase -= 1e6;
    // Idle shuffle so a standing player is never a statue.
    const idleT = this.time * 0.7;
    const breathe = this.noise(idleT) * 0.5 + Math.sin(this.time * 1.6) * 0.5;

    // --- reset joint accumulators -----------------------------------------
    this.jBody.begin();
    this.jPelvis.begin();
    this.jTorso.begin();
    this.jHead.begin();
    this.jGun.begin();

    // =======================================================================
    // POSE: hips
    // =======================================================================
    let hipMul = 0;
    let wSum = 0;
    const addHip = (m: number, w: number): void => {
      hipMul += m * w;
      wSum += w;
    };
    addHip(lerpN(1, CROUCH_RATIO * 1.04, this.crouchBlend), this.wGround);
    addHip(1.03, this.wAir);
    addHip(CROUCH_RATIO * 0.72, this.wSlide);
    addHip(0.95, this.wWall);
    addHip(1.05, this.wGrapple);
    addHip(0.92, this.wDash);
    if (wSum < 1e-3) {
      hipMul = 1;
      wSum = 1;
    }
    hipMul /= wSum;

    const hipY = HIP_Y * hipMul + this.landDip.value - Math.abs(Math.sin(this.gaitPhase * TAU)) * 0.035 * speedN;
    this.jPelvis.move(0, hipY - HIP_Y, 0);

    // Hip sway and counter-rotation. A walk cycle without pelvis rotation reads as a puppet.
    const sway = Math.sin(this.gaitPhase * TAU) * 0.06 * speedN;
    this.jPelvis.move(sway * 0.5, 0, 0);
    this.jPelvis.rot(0, -sway * 1.6, sway * 0.9);

    // =======================================================================
    // POSE: torso — lean, wound hunch, wind-up coil, recoil
    // =======================================================================
    // Lean into acceleration and into turns. Both are momentum tells: an enemy can read
    // which way you are about to go before your position has changed.
    const leanFwd = -0.12 - speedN * 0.30 - speedHi * 0.16 + this.leanPitch.value;
    this.jTorso.rot(leanFwd, 0, 0, this.wGround + this.wDash);
    this.jTorso.rot(-0.42, 0, 0, this.wSlide);
    this.jTorso.rot(-0.1 + clamp(-p.vel.y, -8, 8) * 0.012, 0, 0, this.wAir);
    this.jTorso.rot(-0.34, 0, 0, this.wGrapple);
    this.jTorso.rot(0, 0, clamp(this.leanRoll.value, -0.45, 0.45));

    // Breathing / idle drift.
    this.jTorso.rot(breathe * 0.02, this.noise(idleT + 11) * 0.05, 0, 1 - speedN * 0.7);

    // Wounded: a real postural change, not a tint. Hunched, curled around the wound,
    // shoulder dropped. Visible from behind, from the side, at any distance.
    this.jTorso.rot(0.3, 0.16, 0.1, this.woundBlend);
    this.jPelvis.move(0, -0.05, 0, this.woundBlend);
    // Ragged breathing on top.
    this.jTorso.rot(Math.sin(this.time * 4.6) * 0.03, 0, 0, this.woundBlend);

    // Throw coil: the torso winds away from the throw. This is the telegraph.
    this.jTorso.rot(-0.16, this.throwSide * 0.62, this.throwSide * -0.12, this.windup);
    // Throw release: whip through.
    const whip = Math.sin(clamp01(tThrow / 0.45) * Math.PI * 0.5);
    const follow = window01(tThrow, 0.12, 0.55, 0.2);
    if (this.tlThrow.active || tThrow > 0) {
      this.jTorso.rot(0.22 * whip, -this.throwSide * 0.5 * whip, 0, follow);
    }

    // Catch: brace and absorb.
    this.jTorso.rot(0.18 * (1 - easeOutCubic(tCatch)), 0, 0, tCatch > 0 ? 1 : 0);

    // Recoil.
    this.jTorso.rot(-this.fireKick.value * 0.6, 0, 0);
    // Wall-run roll: the body banks into the wall, matching the camera roll the runner feels.
    if (p.wallNormal) {
      _v.set(p.wallNormal.x, 0, p.wallNormal.z).applyAxisAngle(UP, -this.yawSmooth);
      const side = _v.x >= 0 ? -1 : 1; // normal points away from wall, so wall is on -sign(x)
      this.jBody.rot(0, 0, -side * WALLRUN_CAMERA_ROLL * 1.5, this.wWall);
      this.jTorso.rot(-0.22, side * 0.3, 0, this.wWall);
    }

    // Dash stretch: squash perpendicular to travel, stretch along it. Cartoon physics, but
    // it is what makes a 130ms dash legible at all.
    if (this.wDash > 0.01) {
      const dashK = 1 - clamp01(p.dashElapsedMs / DASH_DURATION_MS);
      const s = this.wDash * dashK;
      _v.set(p.dashDir.x, 0, p.dashDir.z).applyAxisAngle(UP, -this.yawSmooth);
      const fwdC = Math.abs(_v.z);
      const sideC = Math.abs(_v.x);
      this.jBody.scl(1 + sideC * 0.22, 1 - 0.1, 1 + fwdC * 0.24, s);
      this.jTorso.rot(-_v.z * 0.35, 0, _v.x * 0.3, s);
    }

    // Landing squash + death collapse.
    this.jBody.scl(1 + this.landDip.value * -0.35, 1 + this.landDip.value * 0.6, 1 + this.landDip.value * -0.35, 1);

    if (dead) {
      const k = easeOutCubic(tDeath > 0 ? tDeath : 1);
      this.jBody.rot(-1.35 * k, 0, 0.4 * k);
      this.jBody.move(0, -0.42 * k, 0.2 * k);
      this.jBody.scl(1, 1 - 0.25 * k, 1, 1);
    }

    // =======================================================================
    // POSE: head — aim read + look lag
    // =======================================================================
    this.headYawLag = damp(this.headYawLag, clamp(-yawRate * 0.02, -0.4, 0.4), 9, dt);
    this.jHead.rot(-this.pitchSmooth * 0.72, this.headYawLag, 0);
    this.jHead.rot(0.2, 0, 0, this.woundBlend);
    this.jHead.rot(-0.24, 0, 0, this.wSlide);
    this.jHead.rot(this.fireKick.value * 0.5, 0, 0);

    // Head keeps looking at the grapple anchor while swinging — it reads as intent.
    if (this.wGrapple > 0.01 && p.grapplePoint) {
      _v.set(p.grapplePoint.x - p.pos.x, p.grapplePoint.y - p.pos.y, p.grapplePoint.z - p.pos.z).applyAxisAngle(
        UP,
        -this.yawSmooth,
      );
      const yawTo = Math.atan2(-_v.x, -_v.z);
      const pitchTo = Math.atan2(_v.y, Math.hypot(_v.x, _v.z));
      this.jHead.rot(-pitchTo * 0.5 + this.pitchSmooth * 0.72, clamp(yawTo, -0.7, 0.7) * 0.6, 0, this.wGrapple);
    }

    this.jBody.end();
    this.jPelvis.end();
    this.jTorso.end();
    this.jHead.end();

    // =======================================================================
    // LEGS
    // =======================================================================
    this.solveLegs(p, speedN, dead, tDeath);

    // =======================================================================
    // ARMS
    // =======================================================================
    this.solveArms(p, speedN, armed, tThrow, tCatch, tFire, dead, tDeath, breathe);

    // =======================================================================
    // GUN
    // =======================================================================
    this.updateGun(p, armed, dead, tThrow);

    // =======================================================================
    // TAG / RIM / BLEED
    // =======================================================================
    this.updateTag(p, localTeam, armed, wounded, dead, dt);
    this.updateRim(localTeam, wounded, dt);
    this.updateCarrier(armed, dead);
    this.updateBleed(p, dt, wounded, dead);

    // Death: collapse, then stop drawing the body (VFX owns the rest of the moment).
    const collapsed = dead && !this.tlDeath.active;
    this.body.visible = !collapsed;

    // Remember for next frame.
    this.prevYaw = p.yaw;
    this.first = false;
  }

  // -------------------------------------------------------------------------
  // Event detection — everything is inferred from PlayerState deltas
  // -------------------------------------------------------------------------

  private detectEvents(p: PlayerState, dt: number): void {
    if (this.first) {
      this.prevHeld = p.heldGun;
      this.prevFireCd = p.fireCooldownMs;
      this.prevCatchLock = p.catchLockoutMs;
      this.prevThrowCharge = p.throwChargeMs;
      this.prevGrounded = p.grounded;
      this.prevVelY = p.vel.y;
      this.prevVelX = p.vel.x;
      this.prevVelZ = p.vel.z;
      this.prevYaw = p.yaw;
      this.prevHealth = p.healthState;
      return;
    }

    const caught = p.catchLockoutMs > this.prevCatchLock + 0.5;
    const fired = p.fireCooldownMs > this.prevFireCd + 0.5;
    const gained = this.prevHeld < 0 && p.heldGun >= 0;
    const lost = this.prevHeld >= 0 && p.heldGun < 0;
    const healed = this.prevHealth === HealthState.Wounded && p.healthState === HealthState.Healthy;
    const woundedNow = this.prevHealth === HealthState.Healthy && p.healthState === HealthState.Wounded;
    const diedNow = this.prevHealth !== HealthState.Dead && p.healthState === HealthState.Dead;
    const revivedNow = this.prevHealth === HealthState.Dead && p.healthState !== HealthState.Dead;

    if (caught) {
      this.tlCatch.play();
      // A catch chambers — unless the catcher was wounded and took the heal, which spends
      // the round (DESIGN.md §1, "Resolution on Catch"). Catch-raw refuses the heal and
      // keeps the chamber; that shows up here as "still wounded", so the chamber stays.
      if (!this.chamberAuthoritative) this.chambered = !healed;
    } else if (gained) {
      // Not a catch: a floor pickup. PICKUP_FROM_GROUND_CHAMBERS is false — a paperweight.
      if (!this.chamberAuthoritative) this.chambered = false;
    }

    if (fired) {
      this.tlFire.play();
      this.fireKick.kick(1.5);
      if (!this.chamberAuthoritative) this.chambered = false;
    }

    // Losing the gun is a throw in every case that matters visually; a disarm or a drop
    // simply reads as a throw that went wrong, which is exactly what it is.
    if (lost) this.tlThrow.play();

    if (woundedNow) this.flinch.kick(2.4);
    if (diedNow) this.tlDeath.play();
    if (revivedNow) {
      this.tlDeath.stop();
      this.tlDeath.time = 0;
    }

    if (!this.prevGrounded && p.grounded) {
      const impact = clamp01(Math.abs(this.prevVelY) / 18);
      this.landDip.punch(-0.055 - impact * 0.16);
      this.leanPitch.kick(impact * 1.4);
    }

    // Acceleration lean: compare horizontal speed frame to frame.
    const accel = (Math.hypot(p.vel.x, p.vel.z) - Math.hypot(this.prevVelX, this.prevVelZ)) / Math.max(1e-4, dt);
    this.leanPitch.kick(clamp(accel, -60, 60) * dt * 0.05);

    this.prevHeld = p.heldGun;
    this.prevFireCd = p.fireCooldownMs;
    this.prevCatchLock = p.catchLockoutMs;
    this.prevThrowCharge = p.throwChargeMs;
    this.prevGrounded = p.grounded;
    this.prevVelY = p.vel.y;
    this.prevVelX = p.vel.x;
    this.prevVelZ = p.vel.z;
    this.prevHealth = p.healthState;
  }

  // -------------------------------------------------------------------------
  // Legs
  // -------------------------------------------------------------------------

  private solveLegs(p: PlayerState, speedN: number, dead: boolean, tDeath: number): void {
    // Wounded players limp: the injured (right) leg takes a shorter, lower, quicker step.
    const limp = this.woundBlend;
    // Reach and lift are deliberately large now that cadence is capped (see STRIDE_LENGTH):
    // the speed has to go somewhere, and it goes into how FAR each step travels, not how often
    // one happens. Long reach + slow cycle is what reads as a powerful stride; short reach +
    // fast cycle is the "high-gear bicycle" the playtest called out.
    const strideR = (0.34 + speedN * 0.78) * lerpN(1, 0.66, limp);
    const strideL = 0.34 + speedN * 0.78;
    const liftR = (0.09 + speedN * 0.34) * lerpN(1, 0.5, limp);
    const liftL = 0.09 + speedN * 0.34;

    for (const side of [1, -1] as const) {
      const out = side > 0 ? FOOT_TARGET_R : FOOT_TARGET_L;
      _acc.set(0, 0, 0);
      let w = 0;

      // -- gait (grounded) --
      const phase = this.gaitPhase + (side > 0 ? 0 : 0.5);
      footPath(phase, side > 0 ? strideR : strideL, side > 0 ? liftR : liftL, _v);
      // Idle: feet apart, weight shifting.
      const idleSpread = (1 - speedN) * 0.03 * Math.sin(this.time * 1.3 + (side > 0 ? 0 : Math.PI));
      accum(_acc, side * HIP_X * 1.05 + idleSpread, ANKLE_Y + _v.y, _v.z, this.wGround);
      w += this.wGround;

      // -- airborne: legs split and tucked, reaching down on the way back --
      const rise = clamp01(p.vel.y / 7);
      const fall = clamp01(-p.vel.y / 10);
      accum(
        _acc,
        side * HIP_X * 1.25,
        ANKLE_Y + 0.3 * rise + 0.34 * (1 - fall) * 0.4 + 0.06,
        (side > 0 ? -0.22 : 0.2) * (0.4 + rise) - fall * 0.14,
        this.wAir,
      );
      w += this.wAir;

      // -- slide: lead leg out straight, trail leg folded under the hip --
      if (side > 0) accum(_acc, HIP_X * 1.1, ANKLE_Y + 0.02, -0.58, this.wSlide);
      else accum(_acc, -HIP_X * 0.7, ANKLE_Y + 0.2, 0.24, this.wSlide);
      w += this.wSlide;

      // -- wall-run: feet planted on the wall, fast alternating drive --
      let wallX = side * HIP_X;
      if (p.wallNormal) {
        _v2.set(p.wallNormal.x, 0, p.wallNormal.z).applyAxisAngle(UP, -this.yawSmooth);
        const toWall = _v2.x >= 0 ? -1 : 1;
        wallX = toWall * PLAYER_RADIUS * (side === toWall ? 1.05 : 0.55);
      }
      const wallPhase = this.time * 3.2 + (side > 0 ? 0 : Math.PI);
      accum(_acc, wallX, ANKLE_Y + 0.22 + Math.sin(wallPhase) * 0.16, Math.cos(wallPhase) * 0.26, this.wWall);
      w += this.wWall;

      // -- grapple: legs trailing, toes down --
      accum(_acc, side * HIP_X * 1.3, ANKLE_Y + 0.3, 0.3 + (side > 0 ? 0.08 : -0.08), this.wGrapple);
      w += this.wGrapple;

      // -- dash: legs swept opposite the direction of travel --
      _v2.set(p.dashDir.x, 0, p.dashDir.z).applyAxisAngle(UP, -this.yawSmooth);
      accum(
        _acc,
        side * HIP_X * 1.15 - _v2.x * 0.16,
        ANKLE_Y + 0.24,
        _v2.z * 0.3 + (side > 0 ? -0.12 : 0.14),
        this.wDash,
      );
      w += this.wDash;

      if (w < 1e-3) {
        out.set(side * HIP_X, ANKLE_Y, 0);
      } else {
        out.copy(_acc).multiplyScalar(1 / w);
      }

      // Crouch pulls the feet in under the body.
      out.z += this.crouchBlend * 0.06;
      // Death: legs give way.
      if (dead) {
        const k = easeOutCubic(tDeath > 0 ? tDeath : 1);
        out.y = lerpN(out.y, ANKLE_Y + 0.12, k);
        out.z = lerpN(out.z, 0.3, k);
      }
    }

    // Foot targets are authored in body space; the IK runs in pelvis space.
    this.pelvis.updateMatrix();
    _inv.copy(this.pelvis.matrix).invert();

    this.solveLeg(this.thighR, this.shinR, this.footR, FOOT_TARGET_R.applyMatrix4(_inv), 1);
    this.solveLeg(this.thighL, this.shinL, this.footL, FOOT_TARGET_L.applyMatrix4(_inv), -1);
  }

  private solveLeg(
    thigh: THREE.Group,
    shin: THREE.Group,
    foot: THREE.Group,
    target: THREE.Vector3,
    side: number,
  ): void {
    _root.set(side * HIP_X, 0, 0);
    // Knees bend forward (-Z) and splay very slightly outward.
    _pole.set(side * 0.28, 0.05, -1).normalize();
    const d = solveTwoBoneIK(_root, target, THIGH_LEN, SHIN_LEN, _pole, _knee);

    // Clamp the end effector to the reachable distance so the shin never detaches.
    _end.subVectors(target, _root);
    if (_end.lengthSq() > 1e-8) _end.normalize().multiplyScalar(d).add(_root);
    else _end.copy(_root).addScaledVector(STRAIGHT_DOWN, THIGH_LEN + SHIN_LEN);

    aimBone(thigh, _root, _knee);
    aimBone(shin, _knee, _end);

    // Keep the sole roughly parallel to the ground, rolling onto the toe as the leg extends
    // below the neutral ankle height. Without this the feet paddle and the run reads as
    // marionette work.
    const toe = clamp01((ANKLE_Y - _end.y) * 3);
    _quat.setFromAxisAngle(AXIS_X, -0.12 + toe * 0.3);
    foot.quaternion.copy(shin.quaternion).invert().multiply(_quat);
  }

  // -------------------------------------------------------------------------
  // Arms
  // -------------------------------------------------------------------------

  private solveArms(
    p: PlayerState,
    speedN: number,
    armed: boolean,
    tThrow: number,
    tCatch: number,
    tFire: number,
    dead: boolean,
    tDeath: number,
    breathe: number,
  ): void {
    const a = this.armedBlend;
    const pitch = this.pitchSmooth;

    // Aim direction in torso space: forward is -Z, pitched by the player's view.
    _fwd.set(0, Math.sin(pitch), -Math.cos(pitch)).normalize();

    // --- right hand --------------------------------------------------------
    // Armed: out in front on the aim line. Unarmed: swinging with the gait.
    const swing = Math.sin(this.gaitPhase * TAU) * (0.26 + speedN * 0.34);
    const swingLift = Math.abs(Math.sin(this.gaitPhase * TAU)) * 0.1 * speedN;

    HAND_TARGET_R.set(SHOULDER_X * 0.62, SHOULDER_LOCAL_Y - 0.1, 0)
      .addScaledVector(_fwd, 0.34 + this.armedBlend * 0.1)
      .lerp(
        _v.set(
          SHOULDER_X * 0.95 + breathe * 0.01,
          SHOULDER_LOCAL_Y - UPPER_LEN - FORE_LEN * 0.82 + swingLift,
          -swing,
        ),
        1 - a,
      );

    // --- left hand ---------------------------------------------------------
    // Armed: supporting the gun. Unarmed: counter-swinging.
    HAND_TARGET_L.set(-SHOULDER_X * 0.3, SHOULDER_LOCAL_Y - 0.14, 0)
      .addScaledVector(_fwd, 0.3)
      .lerp(
        _v.set(
          -SHOULDER_X * 0.95 - breathe * 0.01,
          SHOULDER_LOCAL_Y - UPPER_LEN - FORE_LEN * 0.82 + swingLift,
          swing,
        ),
        1 - a,
      );

    // --- movement-state overrides -----------------------------------------
    // Sliding: trailing hand drops to the floor for balance.
    if (this.wSlide > 0.01) {
      HAND_TARGET_L.lerp(_v.set(-SHOULDER_X * 1.3, -0.34, 0.24), this.wSlide);
      HAND_TARGET_R.lerp(_v.set(SHOULDER_X * 0.75, SHOULDER_LOCAL_Y - 0.28, -0.34), this.wSlide * (1 - a * 0.6));
    }
    // Airborne: arms out for balance.
    if (this.wAir > 0.01) {
      HAND_TARGET_L.lerp(_v.set(-SHOULDER_X * 1.35, SHOULDER_LOCAL_Y - 0.24, 0.16), this.wAir * (1 - a * 0.4));
      HAND_TARGET_R.lerp(_v.set(SHOULDER_X * 1.2, SHOULDER_LOCAL_Y - 0.3, 0.1), this.wAir * (1 - a * 0.75));
    }
    // Wall-running: inner arm braces against the wall.
    if (this.wWall > 0.01 && p.wallNormal) {
      _v2.set(p.wallNormal.x, 0, p.wallNormal.z).applyAxisAngle(UP, -this.yawSmooth);
      const toWall = _v2.x >= 0 ? -1 : 1;
      const braced = toWall > 0 ? HAND_TARGET_R : HAND_TARGET_L;
      const free = toWall > 0 ? HAND_TARGET_L : HAND_TARGET_R;
      braced.lerp(_v.set(toWall * PLAYER_RADIUS * 1.15, SHOULDER_LOCAL_Y - 0.12, -0.22), this.wWall);
      free.lerp(_v.set(-toWall * SHOULDER_X * 1.1, SHOULDER_LOCAL_Y - 0.36, 0.24), this.wWall * (1 - a * 0.5));
    }
    // Grappling: the free hand reaches for the anchor. Reads as "I am attached to that point".
    if (this.wGrapple > 0.01 && p.grapplePoint) {
      _v2
        .set(p.grapplePoint.x - p.pos.x, p.grapplePoint.y - p.pos.y - SHOULDER_Y, p.grapplePoint.z - p.pos.z)
        .applyAxisAngle(UP, -this.yawSmooth)
        .normalize();
      _v.set(-SHOULDER_X * 0.4, SHOULDER_LOCAL_Y, 0).addScaledVector(_v2, UPPER_LEN + FORE_LEN);
      HAND_TARGET_L.lerp(_v, this.wGrapple);
    }

    // Wounded: the off hand clamps over the wound, and the gun hand sags.
    if (this.woundBlend > 0.01) {
      HAND_TARGET_L.lerp(_v.set(-0.06, 0.16, -TORSO_D * 0.55), this.woundBlend * (1 - this.wGrapple));
      HAND_TARGET_R.lerp(
        _v.set(SHOULDER_X * 0.7, SHOULDER_LOCAL_Y - 0.3, -0.22),
        this.woundBlend * 0.55 * a,
      );
    }

    // --- one-shots ---------------------------------------------------------
    // THROW. Wind-up is driven by the charge timer (so it lasts exactly as long as the
    // thrower holds), release is a timeline. Both are deliberately huge.
    if (this.windup > 0.01) {
      _v.set(SHOULDER_X * 1.15, SHOULDER_LOCAL_Y + 0.28, 0.34);
      HAND_TARGET_R.lerp(_v, this.windup);
      _v.set(-SHOULDER_X * 1.0, SHOULDER_LOCAL_Y + 0.02, -0.34);
      HAND_TARGET_L.lerp(_v, this.windup * 0.8);
    }
    if (tThrow > 0) {
      // Arc the hand from behind the head, through the aim line, to a low follow-through.
      const k = clamp01(tThrow / 0.55);
      const e = easeOutCubic(k);
      _v.set(
        lerpN(SHOULDER_X * 1.1, SHOULDER_X * 0.2, e),
        lerpN(SHOULDER_LOCAL_Y + 0.3, SHOULDER_LOCAL_Y - 0.42, e),
        lerpN(0.32, -0.52, e),
      );
      // After release, drop through and recover.
      const wgt = tThrow < 0.62 ? 1 : 1 - easeOutCubic((tThrow - 0.62) / 0.38);
      HAND_TARGET_R.lerp(_v, wgt);
      HAND_TARGET_L.lerp(_v2.set(-SHOULDER_X * 0.9, SHOULDER_LOCAL_Y - 0.1, -0.2), wgt * 0.5);
    }

    // CATCH. Hands snap out to meet the gun, then settle back with a little overshoot.
    if (tCatch > 0) {
      const reach = 1 - easeOutBack(clamp01(tCatch / 0.7), 2.1);
      _v.set(SHOULDER_X * 0.5, SHOULDER_LOCAL_Y - 0.02, 0).addScaledVector(_fwd, 0.52);
      HAND_TARGET_R.lerp(_v, clamp01(reach));
      _v.set(-SHOULDER_X * 0.5, SHOULDER_LOCAL_Y - 0.05, 0).addScaledVector(_fwd, 0.48);
      HAND_TARGET_L.lerp(_v, clamp01(reach));
    }

    // FIRE. Gun hand kicks up and back, then snaps home.
    if (tFire > 0) {
      const k = Math.exp(-tFire * 7) * (1 - tFire);
      HAND_TARGET_R.y += k * 0.14;
      HAND_TARGET_R.z += k * 0.13;
      HAND_TARGET_L.y += k * 0.1;
      HAND_TARGET_L.z += k * 0.09;
    }

    // PULSE. Unarmed shove: both palms drive forward together. The cooldown counts *down*
    // from PULSE_COOLDOWN_MS, so time-since-pulse is the complement — that way the pose
    // fires on the same frame the sim applies the impulse, not two seconds later.
    const sincePulseMs = PULSE_COOLDOWN_MS - p.pulseCooldownMs;
    if (p.pulseCooldownMs > 0 && sincePulseMs < PULSE_ANIM_MS) {
      const k = bell(clamp01(sincePulseMs / PULSE_ANIM_MS));
      _v.set(SHOULDER_X * 0.55, SHOULDER_LOCAL_Y - 0.06, 0).addScaledVector(_fwd, 0.2 + k * 0.42);
      HAND_TARGET_R.lerp(_v, k);
      _v.set(-SHOULDER_X * 0.55, SHOULDER_LOCAL_Y - 0.06, 0).addScaledVector(_fwd, 0.2 + k * 0.42);
      HAND_TARGET_L.lerp(_v, k);
    }

    // Death: arms go slack.
    if (dead) {
      const k = easeOutCubic(tDeath > 0 ? tDeath : 1);
      HAND_TARGET_R.lerp(_v.set(SHOULDER_X * 1.2, SHOULDER_LOCAL_Y - 0.5, 0.24), k);
      HAND_TARGET_L.lerp(_v.set(-SHOULDER_X * 1.2, SHOULDER_LOCAL_Y - 0.5, 0.24), k);
    }

    // Flinch shakes both hands.
    if (this.flinch.value !== 0) {
      const f = this.flinch.value * 0.05;
      HAND_TARGET_R.x += f;
      HAND_TARGET_L.x -= f;
    }

    this.solveArm(this.upperR, this.foreR, this.handR, HAND_TARGET_R, 1);
    this.solveArm(this.upperL, this.foreL, this.handL, HAND_TARGET_L, -1);

    // Hand orientation. Armed: the gun hand aligns with the aim line so the muzzle points
    // where the player is looking. Unarmed: the wrist follows the forearm.
    orientForward(_quat, _fwd, UP);
    this.handR.quaternion.copy(this.foreR.quaternion).slerp(_quat, clamp01(a * 0.92));
    this.handL.quaternion.copy(this.foreL.quaternion);
  }

  private solveArm(
    upper: THREE.Group,
    fore: THREE.Group,
    hand: THREE.Group,
    target: THREE.Vector3,
    side: number,
  ): void {
    _root.set(side * SHOULDER_X, SHOULDER_LOCAL_Y, 0);
    // Elbows point back, down and out.
    _pole.set(side * 0.55, -0.35, 0.9).normalize();
    const d = solveTwoBoneIK(_root, target, UPPER_LEN, FORE_LEN, _pole, _knee);

    _end.subVectors(target, _root);
    if (_end.lengthSq() > 1e-8) _end.normalize().multiplyScalar(d).add(_root);
    else _end.copy(_root);

    aimBone(upper, _root, _knee);
    aimBone(fore, _knee, _end);
    hand.position.copy(_end);
  }

  // -------------------------------------------------------------------------
  // Held gun
  // -------------------------------------------------------------------------

  private updateGun(p: PlayerState, armed: boolean, dead: boolean, tThrow: number): void {
    // Hide the moment the throw animation releases, not when the server says so. The world
    // gun entity takes over on the same frame, so there is no frame where two guns exist and
    // no frame where none does.
    const released = this.tlThrow.active && tThrow > 0.34;
    const show = armed && !dead && !released;
    this.gunMesh.visible = show;
    if (!show) return;

    styleGun(this.gunMesh, this.team, this.chambered);
    animateGun(this.gunMesh, this.time);

    // Grip alignment: the gun's origin is its grip, so it sits in the palm with a small
    // wrist cant. During a throw wind-up it lifts clear of the body so the pass is legible.
    this.jGun.begin();
    this.jGun.rot(0.12, 0, 0);
    this.jGun.rot(-0.5, 0.3, 0.2, this.windup);
    this.jGun.move(0, 0.06, 0.02, this.windup);
    this.jGun.rot(-this.fireKick.value * 0.55, 0, 0);
    this.jGun.end();
  }

  // -------------------------------------------------------------------------
  // Name tag + state indicators
  // -------------------------------------------------------------------------

  private updateTag(
    p: PlayerState,
    localTeam: number,
    armed: boolean,
    wounded: boolean,
    dead: boolean,
    dt: number,
  ): void {
    // Keep the tag world-aligned rather than yawing with the body.
    this.tagRoot.rotation.y = -this.yawSmooth;
    this.tagRoot.position.y = H + 0.36 - this.crouchBlend * (H - PLAYER_CROUCH_HEIGHT) * 0.75;

    const friendly = p.team === localTeam;
    if (localTeam !== this.prevLocalTeam) {
      this.prevLocalTeam = localTeam;
      // Teammates read through geometry — knowing where your team is, is the whole point of
      // a 2v2. Enemies are depth-tested: you have to actually see them.
      for (const s of [this.tagSprite, this.armedSprite, this.woundSprite]) {
        s.material.depthTest = !friendly;
        s.material.needsUpdate = true;
      }
    }

    // A teammate's plate is allowed through walls, so a teammate must be allowed through
    // walls too — otherwise the plate is a symbol with nothing under it, which is the
    // orphan this pair of rules exists to prevent.
    this.ghostFade = damp(this.ghostFade, friendly && !dead ? 1 : 0, TAG_FADE_RATE, dt);
    const ghostOn = this.ghostFade > 0.012;
    for (let i = 0; i < this.ghostMeshes.length; i++) {
      this.ghostMeshes[i]!.visible = ghostOn;
      (this.ghostMaterials[i]!.uniforms['uFade'] as { value: number }).value = this.ghostFade;
    }

    // FRUSTUM GATE. The plate hangs 2.16m over the feet, so an owner who has dropped off the
    // bottom of the screen can still leave a plate floating in frame with nothing beneath
    // it. Test the OWNER, not the plate, and fade rather than pop so a player crossing the
    // screen edge does not strobe.
    const cam = this.camera ?? this.renderCamera;
    let ownerOnScreen = true;
    if (cam) {
      _projScreen.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
      _frustum.setFromProjectionMatrix(_projScreen);
      _ownerSphere.center.set(p.pos.x, p.pos.y + H * 0.5, p.pos.z);
      _ownerSphere.radius = TAG_OWNER_RADIUS;
      ownerOnScreen = _frustum.intersectsSphere(_ownerSphere);
    }
    this.tagFade = damp(this.tagFade, !dead && ownerOnScreen ? 1 : 0, TAG_FADE_RATE, dt);
    this.tagRoot.visible = this.tagFade > 0.012;
    if (!this.tagRoot.visible) return;

    this.armedSprite.visible = armed && !dead;
    this.woundSprite.visible = wounded && !dead;
    if (this.armedSprite.visible) {
      // The armed pip pulses in LOADED yellow only while the round is live in the chamber.
      // Colour carries the chamber state; opacity only breathes. Fading the icon out would
      // fade its ink outline with it, and the outline is what makes it survive a white wall.
      const hot = this.chambered;
      this.armedSprite.material.color.setHex(hot ? PALETTE.loaded : PALETTE.empty);
      this.armedSprite.material.opacity = hot ? 0.88 + 0.12 * Math.sin(this.time * 8) : 0.85;
    }
    if (this.woundSprite.visible) {
      this.woundSprite.material.opacity = 0.82 + 0.18 * Math.abs(Math.sin(this.time * 3.4));
    }

    // Distance response, when the caller has handed us a camera.
    let scale = 1;
    let alpha = this.tagFade;
    if (cam) {
      cam.getWorldPosition(_v);
      const dist = _v.distanceTo(this.object.position);
      scale = remap(dist, 4, 55, 1, 0.68);
      alpha *= remap(dist, 2.2, 4, 0, 1) * remap(dist, 70, 95, 1, 0);
    }
    // Scaling all three by the same factor keeps the icons in their gutters for free: the
    // `center` displacement is measured in units of the sprite's own scale.
    this.tagSprite.scale.set(TAG_SPRITE_W * scale, TAG_SPRITE_H * scale, 1);
    this.tagSprite.material.opacity = alpha;
    this.armedSprite.scale.set(ARMED_ICON * scale, ARMED_ICON * scale, 1);
    this.woundSprite.scale.set(WOUND_ICON * scale, WOUND_ICON * scale, 1);
    // The icons ride the plate's distance fade. A chevron still burning over a name that has
    // already faded out is a floating symbol attached to nothing.
    this.armedSprite.material.opacity *= alpha;
    this.woundSprite.material.opacity *= alpha;
  }

  // -------------------------------------------------------------------------
  // Rim
  // -------------------------------------------------------------------------

  private updateRim(localTeam: number, wounded: boolean, dt: number): void {
    const enemy = this.team !== localTeam;
    // Enemies burn brighter — finding them is the job. Teammates get enough to keep track of.
    let strength = enemy ? 1.3 : 0.45;
    // A wound pulses. Now you know which enemy is one shot from dead, and so do they.
    if (wounded) strength *= 1 + 0.55 * Math.abs(Math.sin(this.time * 3.4));
    // The throw wind-up flares — an enemy who is paying attention gets to contest the pass.
    strength *= 1 + this.windup * 0.85;

    // Wounded shifts the rim toward hot orange rather than toward PALETTE.danger — danger IS
    // team B's colour, so a magenta player would have signalled nothing at all. Orange reads
    // as "hurt" against cyan and magenta alike.
    const color = wounded ? mixHex(TEAM_COLORS[this.team]!, WOUND_RIM_TINT, 0.62) : TEAM_COLORS[this.team]!;
    for (const m of this.rimMaterials) {
      const u = m.uniforms['uStrength'] as { value: number };
      u.value = damp(u.value, strength, 12, dt);
      (m.uniforms['uColor'] as { value: THREE.Color }).value.setHex(color);
    }

    // The visor is the front-on half of the same read. Healthy, it is a hard clean lamp in
    // team colour and often the first thing you resolve on an approaching body. Wounded, it
    // guts out and shifts toward the wound orange, so a hurt enemy walking at you looks
    // hurt before you can see their posture.
    const visor = this.visorMaterial as THREE.Material & {
      emissive?: THREE.Color;
      emissiveIntensity?: number;
      color?: THREE.Color;
    };
    const flicker = wounded ? 0.72 + 0.28 * Math.abs(Math.sin(this.time * 5.1)) : 1;
    const target = lerpN(VISOR_HOT, VISOR_WOUNDED, this.woundBlend) * flicker;
    if (visor.emissiveIntensity !== undefined) {
      visor.emissiveIntensity = damp(visor.emissiveIntensity, target, 14, dt);
    }
    visor.emissive?.setHex(color);
    visor.color?.setHex(color);

    // VALUE. Everything above is hue or shape, and both can be lost — to a white wall, to a
    // colour-blind player, to a body seen from behind. The team panels themselves also drop
    // in value as the wound sets in, so a hurt player is darker than a healthy one in plain
    // greyscale from any angle at any distance.
    const suit = this.suitMaterial as THREE.Material & { color?: THREE.Color; emissiveIntensity?: number };
    suit.color?.setHex(mixHex(TEAM_COLORS[this.team]!, PALETTE.ink, this.woundBlend * WOUND_SUIT_DARKEN));
    if (suit.emissiveIntensity !== undefined) {
      suit.emissiveIntensity = lerpN(SUIT_EMISSIVE, SUIT_EMISSIVE_WOUNDED, this.woundBlend);
    }
  }

  // -------------------------------------------------------------------------
  // Carrier lamp — "is that player armed?", answered from behind
  // -------------------------------------------------------------------------

  /**
   * Three states, one material, and it only touches the GPU when the state actually flips.
   * Amber and lit means a live round; flat grey means they are carrying a paperweight; dark
   * means empty hands, and empty hands are a *threat* in this game (DESIGN.md §1), so the
   * absence has to be as legible as the presence.
   */
  private updateCarrier(armed: boolean, dead: boolean): void {
    const key = dead || !armed ? 'off' : this.chambered ? 'hot' : 'cold';
    if (key === this.carrierKey) return;
    this.carrierKey = key;

    const m = this.carrierMaterial as THREE.Material & {
      color?: THREE.Color;
      emissive?: THREE.Color;
      emissiveIntensity?: number;
    };
    const hot = key === 'hot';
    // Dark albedo under the hot emissive, for the same reason the gun's energy parts use
    // one: a saturated amber albedo lit by the key on top of a full emissive clips to white,
    // and a white lamp has stopped being an amber lamp.
    m.color?.setHex(
      hot ? mixHex(PALETTE.loaded, PALETTE.ink, 0.55) : key === 'cold' ? mixHex(PALETTE.empty, PALETTE.ink, 0.35) : CARRIER_DARK,
    );
    m.emissive?.setHex(PALETTE.loaded);
    if (m.emissiveIntensity !== undefined) m.emissiveIntensity = hot ? CARRIER_HOT : CARRIER_COLD;
    // Only a live chamber is an emitter. A spent lamp that bloomed would say "armed and
    // dangerous" in the one situation where the truth is "armed and helpless".
    setBloomMask(m, hot ? 1 : 0);
  }

  // -------------------------------------------------------------------------
  // Bleed trail — WOUNDED_BLEED_TRAIL, DESIGN.md §1: "enemy intel"
  // -------------------------------------------------------------------------

  private updateBleed(p: PlayerState, dt: number, wounded: boolean, dead: boolean): void {
    if (!WOUNDED_BLEED_TRAIL) return;

    if (wounded && !dead) {
      this.bleedTimer -= dt;
      if (this.bleedTimer <= 0) {
        this.bleedTimer = BLEED_INTERVAL_SEC;
        this.emitDrip(p);
      }
    }

    let any = false;
    for (let i = 0; i < BLEED_CAPACITY; i++) {
      if (this.bleedLife[i]! <= 0) continue;
      any = true;
      const life = (this.bleedLife[i]! -= dt);
      if (life <= 0) {
        this.bleedAlpha[i] = 0;
        continue;
      }
      const o = i * 3;
      // Fall, then pool on the surface it was emitted over.
      if (this.bleedPos[o + 1]! > this.bleedFloor[i]!) {
        this.bleedVel[o + 1] = this.bleedVel[o + 1]! - 12 * dt;
        this.bleedPos[o] = this.bleedPos[o]! + this.bleedVel[o]! * dt;
        this.bleedPos[o + 1] = Math.max(this.bleedFloor[i]!, this.bleedPos[o + 1]! + this.bleedVel[o + 1]! * dt);
        this.bleedPos[o + 2] = this.bleedPos[o + 2]! + this.bleedVel[o + 2]! * dt;
      } else {
        // Spread slightly as it settles.
        this.bleedSize[i] = Math.min(this.bleedSize[i]! * (1 + dt * 0.5), 0.14);
      }
      this.bleedAlpha[i] = clamp01(life / BLEED_LIFE_SEC) * 0.9;
    }

    this.bleed.visible = any;
    if (!any) return;

    const geo = this.bleed.geometry;
    (geo.attributes['position'] as THREE.BufferAttribute).needsUpdate = true;
    (geo.attributes['aAlpha'] as THREE.BufferAttribute).needsUpdate = true;
    (geo.attributes['aSize'] as THREE.BufferAttribute).needsUpdate = true;

    // The drips are world-space. Cancel the rig transform so they stay where they fell.
    this.object.updateWorldMatrix(true, false);
    this.bleed.matrix.copy(this.object.matrixWorld).invert();
    this.bleed.matrixWorldNeedsUpdate = true;
  }

  private emitDrip(p: PlayerState): void {
    const i = this.bleedCursor;
    this.bleedCursor = (this.bleedCursor + 1) % BLEED_CAPACITY;
    const o = i * 3;
    const n = this.noise(this.time * 9.1);
    const n2 = this.noise(this.time * 7.3 + 40);
    this.bleedPos[o] = p.pos.x + n * 0.22;
    this.bleedPos[o + 1] = p.pos.y + PLAYER_HEIGHT * 0.55;
    this.bleedPos[o + 2] = p.pos.z + n2 * 0.22;
    // Inherit a little of the runner's momentum so the trail streaks behind them.
    this.bleedVel[o] = p.vel.x * 0.18;
    this.bleedVel[o + 1] = -0.4;
    this.bleedVel[o + 2] = p.vel.z * 0.18;
    this.bleedFloor[i] = p.pos.y + 0.03;
    this.bleedLife[i] = BLEED_LIFE_SEC;
    this.bleedAlpha[i] = 0.9;
    this.bleedSize[i] = 0.055 + Math.abs(n) * 0.03;
  }

  private clearBleed(): void {
    this.bleedLife.fill(0);
    this.bleedAlpha.fill(0);
    this.bleed.visible = false;
  }

  // -------------------------------------------------------------------------

  dispose(): void {
    disposeGunMesh(this.gunMesh);
    this.tagTexture.dispose();
    disposeObject3D(this.object);
    for (const m of this.ownedMaterials) m.dispose();
    this.ownedMaterials.length = 0;
  }
}

// ---------------------------------------------------------------------------
// Name tag texture
// ---------------------------------------------------------------------------

const TAG_W = 512;
const TAG_H = 128;

/**
 * Heavy condensed caps with a thick ink outline and a team-coloured underline. It has to be
 * legible over a bone wall, a neon sign and a black pit, so the outline does the work rather
 * than a background panel — panels clutter a 3v3 screen fast.
 */
function makeNameTexture(name: string, teamColor: number): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = TAG_W;
  c.height = TAG_H;
  const g = c.getContext('2d');
  const tex = new THREE.CanvasTexture(c);
  if (!g) return tex;

  const label = (name || 'PLAYER').toUpperCase().slice(0, 14);
  g.clearRect(0, 0, TAG_W, TAG_H);

  g.font = '700 62px "Arial Black", "Helvetica Neue", Impact, sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.lineJoin = 'round';
  g.miterLimit = 2;

  // Ink outline first, then the fill: a cheap, extremely readable sticker look.
  g.strokeStyle = '#0a0b12';
  g.lineWidth = 16;
  g.strokeText(label, TAG_W / 2, TAG_H / 2 - 8);
  g.fillStyle = '#ffffff';
  g.fillText(label, TAG_W / 2, TAG_H / 2 - 8);

  // Team underline.
  const hex = `#${teamColor.toString(16).padStart(6, '0')}`;
  const w = Math.min(TAG_W - 40, g.measureText(label).width + 40);
  g.fillStyle = '#0a0b12';
  g.fillRect((TAG_W - w) / 2 - 3, TAG_H - 39, w + 6, 18);
  g.fillStyle = hex;
  g.fillRect((TAG_W - w) / 2, TAG_H - 36, w, 12);

  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}
